import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface MasterDataItem {
  id: number;
  name: string;
  icon?: string | null;
}

export interface TerritoryItem extends MasterDataItem {
  code: string;
}

/**
 * The query key of every master data collection; its endpoint is the key with
 * a leading slash. refreshServerDataAfterRestore refetches this list on each
 * app start, so a collection added here is covered without a second edit.
 */
export const MASTER_DATA_QUERY_KEYS = [
  'categories',
  'applications',
  'indications',
  'marketing-claims',
  'partners',
  'territories',
] as const;

type MasterDataQueryKey = (typeof MASTER_DATA_QUERY_KEYS)[number];

const SYNC_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours

function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
}

function useMasterDataCollection(key: MasterDataQueryKey, enabled = true) {
  return useQuery<MasterDataItem[]>({
    queryKey: [key],
    queryFn: async () => sortByName(await api<MasterDataItem[]>(`/${key}`)),
    enabled,
    // Only poll while online — an offline tick would fail against the
    // NetworkOnly SW (VIVATISPDB-305) and provides no benefit here since
    // master data is not mutated locally.
    refetchInterval: () =>
      typeof navigator !== 'undefined' && navigator.onLine ? SYNC_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
}

export function useCategories(enabled = true) {
  return useMasterDataCollection('categories', enabled);
}

export function useApplications(enabled = true) {
  return useMasterDataCollection('applications', enabled);
}

export function useIndications(enabled = true) {
  return useMasterDataCollection('indications', enabled);
}

export function useMarketingClaims(enabled = true) {
  return useMasterDataCollection('marketing-claims', enabled);
}

export function usePartners(enabled = true) {
  return useMasterDataCollection('partners', enabled);
}

export function useTerritories(enabled = true) {
  const key: MasterDataQueryKey = 'territories';

  return useQuery<TerritoryItem[]>({
    queryKey: [key],
    queryFn: async () => sortByName(await api<TerritoryItem[]>(`/${key}`)),
    enabled,
    refetchInterval: () =>
      typeof navigator !== 'undefined' && navigator.onLine ? SYNC_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
}
